import React, { useEffect, useState } from 'react';
import { Button, Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faEdit, faFileAlt, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

//Components
import MainCard from '../../../../../components/MainCard';
import TextField from '../../../../../components/TextField';
import RichTextEditor from '../../../../../components/RichTextEditor';

//Api
import documento_api from '../../../../../api/documento_api';
import variaveisSistema_api from '../../../../../api/variaveisSistema_api';

//Helpers
import { showMessage } from '../../../../../helpers/message';
import { validarCampos, objCampo } from '../../../../../helpers/ValidadorDeCampos';
import { downloadBase64File, downloadZip } from '../../../../../helpers/downloadFile';
import { getSessionCookie } from '../../../../../helpers/cookies';

//Context
import { useAuth } from '../../../../../context/auth';
import ModalCustomized from '../../../../../components/Modal/Modal';
import TabelaListagemFichas from '../../../../../components/TabelaListagemFichas/TabelaListagemFichas';

function initialStateDocumento(secretaria_Id) {
    return {
        identificacao: "",
        orientacoes: "",
        html: "",
        arquivo: "",
        status: "Ativo",
        tipo: "Avulso",
        secretaria_Id: secretaria_Id,
        imagensConteudo: []
    }
}
export default function CadastroDocumento({ id, btnVoltar, editavel = false }) {
    //Dados de autenticação
    const session = getSessionCookie();

    //Estado de controle dos campos de erro
    const [errors, setErrors] = useState({});

    //Estado de controle dos campos de cadastro de documento
    const [documento, setDocumento] = useState(initialStateDocumento(session.secretaria_Id));

    //State de lista de Variaveis do sistema
    const [listaVariaveisSistema, setListaVariaveisSistema] = useState([]);

    //State para visibilidade do modal
    const [isModalListaVariaveis, setIsModalListaVariaveis] = useState(false);

    //State para alterar o state do modal
    const toggleModalListaVariaveis = () => setIsModalListaVariaveis(!isModalListaVariaveis);

    const { setLoading, loading } = useAuth();

    //Estado de controle dos campos obrigatorios 
    const [campos, setCampos] = useState([
        objCampo({ name: "identificacao", required: true }),
        objCampo({ name: "status", required: true }),
        objCampo({ name: "tipo", required: true }),
    ]);

    //Função de load do documento para atualização
    const loadDocumento = (id = null) => {
        if (id != null || documento.id != null) {
            setLoading(true);
            documento_api.getItem(documento.id ?? id).then(response => {
                response.data.imagensConteudo = [];
                setDocumento(response.data);
            })
                .catch(e => showMessage("Erro", e.message, "error", null))
                .finally(f => setLoading(false));
        }
    }

    const loadListaVariaveisSistema = () => {
        setLoading(true);
        variaveisSistema_api.getList()
            .then(response => {
                setListaVariaveisSistema(response.data);
            })
            .catch(e => showMessage("Erro", e.message, "error", null))
            .finally(() => setLoading(false));
    }

    //Cabeçalhos que a tabela terá
    const headersListaDeVariaveis = [
        { value: "Código", objectValue: "codigo", align: "left" },
        { value: "Descrição", objectValue: "descricao", align: "left" }
    ];

    useEffect(() => {
        loadDocumento(id);
        loadListaVariaveisSistema();
    }, []);

    //Função de onChange dos campos de cadastro
    const onChange = (e) => {
        const { name, value } = e.target;
        const _documento = {
            ...documento,
            [name]: value
        };

        //Validando erros no campo especifico para checar em tempo de digitação se ele possui algum erro
        const _campos = campos.filter(campo => campo.name === name);
        const fieldError = validarCampos(_documento, _campos);

        const _errors = { ...errors };
        delete _errors[name];
        if (fieldError[1].length === 0)
            setErrors({ ..._errors });
        else
            setErrors({ ..._errors, ...fieldError[0] });

        setDocumento(_documento);
    }

    //Função de on change do campo de tipo
    const onChangeTipo = (e) => {
        const { name, value } = e.target;
        const _campos = campos.filter(campo => campo.name !== "arquivo" && campo.name !== "html");
        const _documento = {
            ...documento,
            [name]: value
        };
        if (value === "Personalizado") {
            _documento.arquivo = "";
            setCampos([
                ..._campos,
                objCampo({ name: "html", required: true }),
            ]);
        }
        if (value === "Arquivo") {
            _documento.html = "";
            setCampos([
                ..._campos,
                objCampo({ name: "arquivo", required: false })
            ]);
        }

        setDocumento(_documento);
    }
    //Função de onChange do arquivo de anexo
    const onChangeAnexo = (e) => {
        const FR = new FileReader();

        FR.addEventListener("load", function (evt) {
            const base64 = evt.target.result;
            setDocumento({
                ...documento,
                arquivo: base64
            });
        });
        if (e.target.files)
            FR.readAsDataURL(e.target.files[0]);
    }

    const setImage = (base64, e) => {
        setDocumento(anterior => ({
            ...anterior,
            [e.target.name]: e.target.value,
            imagensConteudo: [...anterior.imagensConteudo ?? [], base64]
        }));
    }

    //Função para dar submit nos dados do documento
    const onSubmit = () => {
        //Realizando a validação dos campos obrigatorios
        const _errors = validarCampos(documento, campos);
        setErrors(_errors[0]);

        if (_errors[1].length > 0)
            return true;

        //Realizando o cadastro/edição do documento
        setLoading(true);
        const _documento = { ...documento }

        if (_documento.id) {
            documento_api.put(_documento).then(response => {
                showMessage("Sucesso", "Operação realizada com sucesso", "success", btnVoltar);
            })
                .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
                .finally(() => setLoading(false));
        } else {
            documento_api.post(_documento).then(response => {
                showMessage("Sucesso", "Operação realizada com sucesso", "success", btnVoltar);
            })
                .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
                .finally(() => setLoading(false));
        }
    }

    function handleDownload(documento) {
        const extensao = documento?.arquivo?.split('.').pop()?.toLowerCase();
        if (extensao === "zip")
            downloadZip(documento.base64, documento.linkArquivo)
        else
            downloadBase64File(documento.base64, documento.identificacao);
    }

    return (
        <>
            {isModalListaVariaveis &&
                <ModalCustomized
                    toggle={toggleModalListaVariaveis}
                    isOpen={isModalListaVariaveis}
                    btnClose={toggleModalListaVariaveis}
                    title={"Lista de variáveis"}
                >
                    <TabelaListagemFichas
                        options={{ print: false, search: true, pagination: true }}
                        headers={headersListaDeVariaveis}
                        items={listaVariaveisSistema.filter(v => v.tipo == "Infração")}
                    />
                </ModalCustomized>
            }
            <Button onClick={btnVoltar} className='px-4'><FontAwesomeIcon icon={faAngleLeft} className='me-2' /> Voltar</Button>
            <MainCard>

                <Row className='d-flex align-items-center justify-content-between mb-4'>
                    <Col md="6" lg="6" className='text-start'>
                        <h5 className='text-secondary'>Adicionar Documento</h5>
                    </Col>
                    {documento.tipo == "Personalizado" &&
                        <>
                            <Col md="6" lg="6" className='text-end'>
                                <Button
                                    color='dark'
                                    className='text-white fw-bold'
                                    onClick={() => setIsModalListaVariaveis(true)}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />  Visualizar lista de variáveis
                                </Button>
                            </Col>
                        </>
                    }
                </Row>
                <Row>
                    <Col md="12" lg="5">
                        <TextField
                            label="* Identificação"
                            name={"identificacao"}
                            onChange={onChange}
                            value={documento.identificacao}
                            invalid={!!errors.identificacao}
                            maxLength={100}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg="4">
                        <TextField
                            type={"select"}
                            label="* Tipo"
                            name={"tipo"}
                            options={[
                                { value: "Avulso", label: "Avulso" },
                                { label: "Personalizado", value: "Personalizado" },
                                { value: "Arquivo", label: "Arquivo" }
                            ]}
                            onChange={onChangeTipo}
                            value={documento.tipo}
                            invalid={!!errors.tipo}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg="3">
                        <TextField
                            type={"select"}
                            label="* Status"
                            name={"status"}
                            options={
                                [
                                    { label: "Ativo", value: "Ativo" },
                                    { value: "Inativo", label: "Inativo" }
                                ]
                            }
                            onChange={onChange}
                            value={documento.status}
                            invalid={!!errors.status}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg="12">
                        <TextField
                            label="Orientações"
                            name={"orientacoes"}
                            type={"textarea"}
                            onChange={onChange}
                            value={documento.orientacoes}
                            invalid={!!errors.orientacoes}
                            maxLength={500}
                            disabled={!editavel}
                        />
                    </Col>
                    {documento.tipo === "Arquivo" &&
                        <>
                            <Col md={documento.arquivo ? "9" : "12"} lg={documento.arquivo ? "9" : "12"}>
                                <TextField
                                    label="Arquivo"
                                    name={"arquivo"}
                                    type={"file"}
                                    onChange={onChangeAnexo}
                                    value={documento.arquivo}
                                    invalid={!!errors.arquivo}
                                    accept="application/pdf"
                                    disabled={!editavel}
                                />
                            </Col>
                            {documento.arquivo &&
                                <Col md="3" lg="3" >
                                    <Button
                                        className='w-100 mt-2'
                                        color='danger'
                                        onClick={() => handleDownload(documento)}>
                                        <FontAwesomeIcon icon={faFilePdf} /> Download PDF
                                    </Button>
                                </Col>
                            }
                        </>
                    }
                    {documento.tipo === "Personalizado" &&
                        <>
                            {!loading &&
                                <Col md="12" lg="12">
                                    <RichTextEditor
                                        name={"html"}
                                        saveImage={setImage}
                                        value={documento.html}
                                        onChange={onChange}
                                        invalid={!!errors.html}
                                    />
                                </Col>

                            }
                        </>
                    }
                    <Col md="12" lg="12" className={`d-flex align-items-center justify-content-center mt-4 ${editavel ? "" : "d-none"}`}>
                        <Button
                            className='px-4'
                            color={id ? 'warning text-white' : 'success'}
                            onClick={onSubmit}
                        >
                            {id ? <><FontAwesomeIcon icon={faEdit} /> Atualizar</> : "+ Cadastrar"}
                        </Button>
                    </Col>
                </Row>
            </MainCard>
        </>
    );
}